import { Card, Collapse, Flex, Form, Typography } from "antd"
import { useContext, useEffect, useState } from "react"
import { AuthenticationContext } from "../../../../../AuthenticationContext"
import { usePartialEditWorkareaGroupMutation, useWorkareaGroupsQuery, WorkareaGroupMutationData } from "../../../../../domain/workareas/groupsQueries"
import ChangeOnBlurInput from "../../../../common/ChangeOnBlurInput"
import VSpace from "../../../../common/VSpace"
import { ItemsContext } from "../../../../Items/ItemsRoot"
import RichTextEditor from "../../../../RichTextEditor/RichTextEditor"
import JobPlanContext from "../../JobPlanContext"
import { arePricingFieldsLockedForGroup, getFinalPriceChangesOnNotApproved, mapFinalPriceChangesToRequestFormat } from "../../List/workareaGroupsListModel"
import ApprovalStateSwitch from "../ApprovalStateSwitch"
import DrawerHeader from "../DrawerHeader"
import QboSalesTaxSelector from "../QboSalesTaxSelector"
import NotesEditor from "../WorkareaEditor/NotesEditor"
import PricingTypeSelector from "../WorkareaEditor/PricingTypeSelector"
import ProposalDescriptionAttachments from "../ProposalDescriptionAttachments"
import DescriptionCard from "../DescriptionCard"
import FinalPriceWarningsModal from "./FinalPriceWarningsModal"
import * as EditorModel from "./groupEditorModel"
import ShowTaxInFinalPriceControl, { isTaxDisplayedForPricingType } from "../WorkareaEditor/ShowTaxInFinalPriceControl"
import TaxRateSelector from "../../../../invoicing/TaxRates/TaxRateSelector"

const { Text } = Typography

type FinalPriceWarningFor = 'isApproved' | 'isOnHold' | ''

const GroupEditor = () => {
  const { currentUser } = useContext(AuthenticationContext)
  const { currentSelection, transactions, setIsTotalsCalculating, invoicingSystem } = useContext(JobPlanContext)
  const { jobData: { jobId }, workareaSummaries } = useContext(ItemsContext)
  const [showFinalPriceWarningFor, setShowFinalPriceWarningFor] = useState<FinalPriceWarningFor>('')

  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)
  const partialEditWorkareaGroupMutation = usePartialEditWorkareaGroupMutation()

  const group = workareaGroupsQuery.data?.find(g => g.id === currentSelection?.groupId)
  const isAutosaving = partialEditWorkareaGroupMutation.isLoading
  const isSaved = partialEditWorkareaGroupMutation.isSuccess

  useEffect(() => {
    setIsTotalsCalculating(false)
  }, [setIsTotalsCalculating])
  
  if (!group) {
    return <>Workarea group not found.</>
  }
  
  const pricingFieldsLocked = arePricingFieldsLockedForGroup(group, transactions)
  const finalPriceChanges = getFinalPriceChangesOnNotApproved(group, workareaSummaries)

  const showQboSalesTaxSelector = currentUser?.isTenantConnectedToQuickBooks === true
    && currentUser?.tenantQuickbooksCompanyCountryCode !== 'US'
  const showTaxRateSelector = EditorModel.shouldShowTaxRateSelector(invoicingSystem, group)

  const handleGroupUpdate = (data: WorkareaGroupMutationData) => {
    if (data.isValidUpdate) {
      partialEditWorkareaGroupMutation.mutate(data)
    }
  }

  const checkApprovalChange = () => {
    if (finalPriceChanges.length > 0) {
      setShowFinalPriceWarningFor('isApproved')
    } else {
      handleGroupUpdate(EditorModel.updateGroupIsApproved(group, !group.isApproved, []))
    }
  }

  const checkOnHoldChange = () => {
    if (finalPriceChanges.length > 0) {
      setShowFinalPriceWarningFor('isOnHold')
    } else {
      handleGroupUpdate(EditorModel.updateGroupIsOnHold(group, !group.isOnHold, []))
    }
  }

  return <div>
    <DrawerHeader isAutosaving={isAutosaving} isSaved={isSaved} />
    <div style={{ height: 'calc(100vh - 116px)', overflowY: 'auto' }}>
      <div style={{ padding: '24px' }}>
        <Flex vertical gap="large">
          <Card
            title='Description'
          >
            <Form layout="vertical">
              <Flex gap="large" align="start" wrap="wrap">
                <Form.Item label="This work area group is on hold">
                  <ApprovalStateSwitch
                    value={group.isOnHold}
                    onChange={() => checkOnHoldChange()}
                    canEditGroupFields={true}
                    hasInvoices={pricingFieldsLocked}
                    isApproved={group.isApproved}
                  />
                </Form.Item>
                <Form.Item label="This work area group is approved">
                  <ApprovalStateSwitch
                    value={group.isApproved}
                    onChange={() => checkApprovalChange()}
                    canEditGroupFields={true}
                    hasInvoices={pricingFieldsLocked}
                    isApproved={group.isApproved}
                  />
                </Form.Item>
              </Flex>
              <Form.Item label="Group Name">
                <ChangeOnBlurInput
                  value={group.name}
                  highlightWhenEmpty
                  onBlurChange={x => handleGroupUpdate(EditorModel.updateGroupName(group, x))}
                  maxLength={100}
                />
              </Form.Item>
              <Form.Item label="Pricing Type">
                <PricingTypeSelector
                  value={group.pricingType}
                  onChange={x => handleGroupUpdate(EditorModel.updateGroupPricingType(group, x))}
                  canEditGroupFields={true}
                  isPricingLocked={pricingFieldsLocked}
                  excludePerVisit={true}
                  isApproved={group.isApproved}
                />
              </Form.Item>
              {isTaxDisplayedForPricingType(group.pricingType) && <Form.Item label="">
                <ShowTaxInFinalPriceControl
                  pricingType={group.pricingType}
                  isEditingGroupLevel={true}
                  canEditGroupFields={true}
                  showTaxInFinalPrice={group.showTaxInFinalPrice}
                  onChange={value => handleGroupUpdate(EditorModel.updateShowTaxInFinalPrice(group, value))}
                />
              </Form.Item>}
              {showQboSalesTaxSelector && <Form.Item label="Sales Tax">
                <QboSalesTaxSelector
                  selectedSalesTaxId={group.qboSalesTaxId}
                  canEditGroupFields={true}
                  onChange={x => handleGroupUpdate(EditorModel.updateGroupQboSalesTaxId(group, x))}
                />
              </Form.Item>}
              {showTaxRateSelector && <Form.Item label="Tax Rate">
                <TaxRateSelector
                  invoicingSystem={invoicingSystem}
                  jobId={jobId}
                  selectedTaxRateId={group.taxRateId}
                  canEditGroupFields={true}
                  onChange={x => handleGroupUpdate(EditorModel.updateGroupTaxRateId(group, x ?? null))}
                />
              </Form.Item>}
            </Form>
          </Card>

          <DescriptionCard
            owner="group"
            proposal={<>
              <NotesEditor
                workareaOrGroupId={group.id}
                notes={group.customerAndInvoiceNotes}
                onChange={notes => handleGroupUpdate(EditorModel.updateGroupCustomerAndInvoiceNotes(group, notes))}
                forceUpdateCounter={0}
              />

              <ProposalDescriptionAttachments groupId={group.id} />

              <VSpace>
                <strong>Work Area Descriptions and Attachments</strong>
                <Text type="secondary">Grouped work area descriptions are not displayed on the proposal, but their attachments are, alongside the group's own.</Text>
                <Collapse
                  items={group.workareas.map(workarea => ({
                    key: workarea.id.toString(),
                    label: workarea.name,
                    children: <VSpace>
                      <RichTextEditor value={workarea.customerAndInvoiceNotes} disabled />
                      <ProposalDescriptionAttachments workareaId={workarea.id} readOnly />
                    </VSpace>
                  }))}
                />
              </VSpace>
            </>}
            invoice={<NotesEditor
              workareaOrGroupId={group.id}
              notes={group.invoiceDescription ?? ''}
              onChange={description => handleGroupUpdate(EditorModel.updateGroupInvoiceDescription(group, description))}
              forceUpdateCounter={0}
            />}
          />
        </Flex>
      </div>
    </div>
    <FinalPriceWarningsModal 
      isOpen={showFinalPriceWarningFor !== ''}
      onCancel={() => setShowFinalPriceWarningFor('')}
      onConfirm={(keepFinalPrices) => {
        setShowFinalPriceWarningFor('')
        const changes = mapFinalPriceChangesToRequestFormat(finalPriceChanges, keepFinalPrices)
        if (showFinalPriceWarningFor === 'isApproved') {
          handleGroupUpdate(EditorModel.updateGroupIsApproved(group, !group.isApproved, changes))
        } else {
          handleGroupUpdate(EditorModel.updateGroupIsOnHold(group, !group.isOnHold, changes))
        }
      }}
      finalPriceChanges={finalPriceChanges}
    />
  </div>
}

export default GroupEditor